import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE } from "../data/site";

/**
 * Per-page SEO. Renders title, meta description, canonical, Open Graph,
 * Twitter card tags, and any JSON-LD structured data passed via `jsonLd`.
 *
 * @param {string} title       - Page <title> (full, not suffixed automatically)
 * @param {string} description - Meta description
 * @param {string} path        - Route path, e.g. "/about" (used for canonical/OG url)
 * @param {string} [image]     - Absolute OG image URL
 * @param {object|object[]} [jsonLd] - One or more schema.org objects
 */
const Seo = ({ title, description, path = "/", image, jsonLd }) => {
  const url = `${SITE.domain}${path}`;
  const ogImage = image || `${SITE.domain}/og-image.png`;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE.name} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
